const RECAPTCHA_SCRIPT_ID = 'parkourtheory-recaptcha-script';

let recaptchaLoadPromise: Promise<void> | null = null;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
    };
  }
}

function getRecaptchaSiteKey(): string {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    throw new Error('REACT_APP_RECAPTCHA_SITE_KEY is not configured');
  }
  return siteKey;
}

export function loadRecaptchaScript(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('reCAPTCHA can only be loaded in a browser environment');
  }

  if (window.grecaptcha) {
    return Promise.resolve();
  }

  if (recaptchaLoadPromise) {
    return recaptchaLoadPromise;
  }

  recaptchaLoadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      RECAPTCHA_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load reCAPTCHA script')),
        { once: true }
      );
      return;
    }

    const siteKey = getRecaptchaSiteKey();
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.body.appendChild(script);
  }).catch((error: unknown) => {
    recaptchaLoadPromise = null;
    const failedScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (failedScript) {
      failedScript.remove();
    }
    throw error;
  });

  return recaptchaLoadPromise;
}

export async function getRecaptchaToken(action: string): Promise<string> {
  await loadRecaptchaScript();

  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) {
    throw new Error('reCAPTCHA did not initialize correctly');
  }

  const siteKey = getRecaptchaSiteKey();
  return await new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
    });
  });
}
